import React, { useEffect, useMemo, useState, useCallback } from "react";
import { format, parseISO, isToday as fnsIsToday } from "date-fns";
import { mdy, mdyDow } from "@/lib/dateFmt";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import CallOutDialog from "@/components/CallOutDialog";
import {
  CalendarDays, Clock, StickyNote, Users2, Phone, PhoneCall, Info,
  AlertTriangle, Sun, Moon, CalendarClock,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const fmtTime = (t) => {
  if (!t) return "—";
  try {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
  } catch { return t; }
};

const WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function MyDashboardPage({ facilityId, facilityLabel }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callOutOpen, setCallOutOpen] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await api.get(`/my-dashboard?facility_id=${facilityId}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load your dashboard");
    } finally { setLoading(false); }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const today = data?.today_shift;
  const hasShiftToday = !!(today && today.shift_code && !today.is_absent);

  if (loading && !data) {
    return <div className="p-6 text-sm text-slate-500">Loading your dashboard…</div>;
  }

  return (
    <div className="space-y-4 pb-6" data-testid="my-dashboard">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-2xl sm:text-3xl">
            Hi, {user?.name?.split(" ")[0] || "there"}
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            {mdyDow(new Date())} · {facilityLabel || data?.facility_label || "Your facility"}
          </div>
        </div>
      </div>

      {/* Top row: Today card + On-call card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* My schedule today */}
        <div
          className={`lg:col-span-2 rounded-2xl border shadow-sm p-4 sm:p-5 ${
            hasShiftToday ? "border-blue-200 bg-gradient-to-br from-blue-50 to-white" : "border-slate-200 bg-white"
          }`}
          data-testid="dash-today-card"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-blue-700">
            <Sun size={14} /> My schedule today
          </div>
          {hasShiftToday ? (
            <>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <div className="font-display font-bold text-3xl tabular-nums" data-testid="dash-today-code">
                  {today.shift_code}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    {today.label || `Shift ${today.shift_code}`}
                  </div>
                  <div className="text-sm text-slate-600 inline-flex items-center gap-1.5">
                    <Clock size={13} /> {fmtTime(today.start)} – {fmtTime(today.end)}
                    {today.hours != null && (
                      <span className="ml-2 text-slate-500">· {today.hours}h</span>
                    )}
                  </div>
                </div>
              </div>
              {today.notes?.length > 0 && (
                <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="dash-today-notes">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-amber-800">
                    <StickyNote size={12} /> Note{today.notes.length === 1 ? "" : "s"} for today
                  </div>
                  <ul className="mt-1.5 text-sm text-amber-900 space-y-1">
                    {today.notes.map((n) => (
                      <li key={n.id} className="leading-snug">{n.content}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : today?.is_absent ? (
            <div className="mt-2 text-slate-600 text-sm">
              You're marked absent today. Reach out to your manager if that's not right.
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-slate-500">
              <Moon size={16} /> <span className="text-sm">You're not scheduled today — enjoy the day off.</span>
            </div>
          )}
        </div>

        {/* On-call manager */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5" data-testid="dash-oncall-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-500">
            <Phone size={14} /> On-call manager today
          </div>
          {data?.oncall ? (
            <div className="mt-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full grid place-items-center text-white font-bold text-sm shrink-0 shadow-sm"
                  style={{ background: data.oncall.color || "#64748b" }}
                  data-testid="dash-oncall-badge"
                >
                  {data.oncall.initials}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate" data-testid="dash-oncall-name">
                    {data.oncall.name}
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums">{data.oncall.phone}</div>
                </div>
              </div>
              <a
                href={`tel:${(data.oncall.phone || "").replace(/[^\d+]/g, "")}`}
                className="mt-3 inline-flex items-center justify-center w-full gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 px-3 transition-colors"
                data-testid="dash-oncall-call"
              >
                <PhoneCall size={14} /> Call {data.oncall.name.split(" ")[0]}
              </a>
              {(!user?.primary_facility_id || user?.primary_facility_id === facilityId) && (
                <button
                  onClick={() => setCallOutOpen(true)}
                  className="mt-2 inline-flex items-center justify-center w-full gap-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 text-sm font-semibold py-2 px-3 transition-colors"
                  data-testid="dash-callout-open"
                >
                  <AlertTriangle size={14} /> Need to call out?
                </button>
              )}
            </div>
          ) : (
            <div className="mt-3 text-sm text-slate-500">
              No manager is on-call today. Contact your manager directly.
            </div>
          )}
        </div>
      </div>

      {/* My schedule this week */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="dash-week-card">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center gap-2">
          <CalendarDays size={16} className="text-slate-500" />
          <div className="font-display font-semibold text-lg">My schedule this week</div>
          <div className="text-xs text-slate-500 ml-auto">
            {data?.week_start && mdy(data.week_start)} – {data?.week_end && mdy(data.week_end)}
          </div>
        </div>
        <div className="grid grid-cols-7 divide-x divide-slate-100">
          {(data?.week || []).map((d, i) => {
            const dObj = parseISO(d.date);
            const isDayToday = fnsIsToday(dObj);
            const worked = !!d.shift_code;
            return (
              <div
                key={d.date}
                className={`p-2 sm:p-3 min-h-[92px] flex flex-col ${isDayToday ? "bg-blue-50/60" : ""}`}
                data-testid={`dash-week-${d.date}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  {WEEK_LABELS[i]}
                </div>
                <div className={`text-sm font-bold tabular-nums ${isDayToday ? "text-blue-700" : "text-slate-800"}`}>
                  {format(dObj, "d")}
                </div>
                {worked ? (
                  <div className="mt-1.5">
                    <div className={`inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-bold ${
                      d.is_absent
                        ? "bg-red-100 text-red-700 border border-red-200"
                        : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                    }`}>
                      {d.shift_code}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 tabular-nums leading-tight">
                      {fmtTime(d.start)}<br/>{fmtTime(d.end)}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-[10px] text-slate-400">Off</div>
                )}
                {d.notes?.length > 0 && (
                  <div className="mt-auto pt-1 flex items-center gap-1 text-[10px] text-amber-700" title={d.notes.map(n => n.content).join(" · ")}>
                    <StickyNote size={10} /> {d.notes.length}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Who's working with me today */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="dash-coworkers-card">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center gap-2">
          <Users2 size={16} className="text-slate-500" />
          <div className="font-display font-semibold text-lg">Who's working with me today</div>
          <span className="ml-auto text-xs text-slate-500 tabular-nums">
            {data?.coworkers_today?.length || 0} on shift
          </span>
        </div>
        {data?.coworkers_today?.length ? (
          <div className="divide-y divide-slate-100">
            {data.coworkers_today.map((c) => (
              <div key={c.employee_id} className="p-3 sm:p-4 flex items-center gap-3" data-testid={`dash-coworker-${c.employee_id}`}>
                <Avatar className="w-9 h-9 shrink-0">
                  <AvatarImage src={c.avatar} />
                  <AvatarFallback>{c.name?.[0] || "?"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.label || `Shift ${c.shift_code}`} · {fmtTime(c.start)} – {fmtTime(c.end)}
                  </div>
                </div>
                <div className="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200 tabular-nums shrink-0">
                  {c.shift_code}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-sm text-slate-500 text-center">
            Looks like you're the only one scheduled today.
          </div>
        )}
      </div>

      <MyUpcomingMeetings facilityId={facilityId} />

      <MeetingCoverBoard facilityId={facilityId} />

      <CallOutDialog
        open={callOutOpen}
        onClose={() => setCallOutOpen(false)}
        facilityId={facilityId}
        today={today}
        oncall={data?.oncall}
        onSubmitted={() => { load(); toast.success("Call-out submitted. Take care of yourself."); }}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Meeting cover-signup board. Only renders anything if the caller has openings
// they qualify for (trained + no-OT).
function MeetingCoverBoard({ facilityId }) {
  const [openings, setOpenings] = React.useState([]);
  const [busy, setBusy] = React.useState({});

  const load = React.useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await api.get(`/meetings/cover-board?facility_id=${facilityId}`);
      setOpenings(data || []);
    } catch { setOpenings([]); }
  }, [facilityId]);
  React.useEffect(() => { load(); }, [load]);

  const volunteer = async (o) => {
    const key = `${o.meeting_id}:${o.session.id}:${o.attendee_id}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post(`/meetings/${o.meeting_id}/cover-signup`, {
        session_id: o.session.id, covers_employee_id: o.attendee_id,
      });
      toast.success(`Thanks for covering ${o.attendee_name}!`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && detail.message) || detail || "Failed to sign up");
    }
    setBusy((b) => ({ ...b, [key]: false }));
  };

  if (openings.length === 0) return null;
  return (
    <div className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50/50 p-4" data-testid="dash-cover-board">
      <div className="text-xs uppercase tracking-widest font-bold text-fuchsia-700 mb-2 inline-flex items-center gap-1">
        <CalendarClock size={14} /> Meetings need coverage · {openings.length} opening{openings.length === 1 ? "" : "s"}
      </div>
      <div className="text-xs text-slate-600 mb-3">
        These shifts overlap with meetings your coworkers have to attend. You're trained + it won't create OT — jump in?
      </div>
      <div className="space-y-2">
        {openings.map((o) => {
          const key = `${o.meeting_id}:${o.session.id}:${o.attendee_id}`;
          return (
            <div key={key} className="flex items-center gap-3 bg-white border border-fuchsia-200 rounded-lg p-2" data-testid={`cover-opening-${o.session.id}-${o.attendee_id}`}>
              <span className="w-2 h-8 rounded" style={{ background: o.session.color }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {o.attendee_name} · {o.dept}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {o.meeting_name} · {o.session.date} · {o.session.start_time}–{o.session.end_time}
                </div>
              </div>
              <button
                onClick={() => volunteer(o)}
                disabled={!!busy[key]}
                data-testid={`volunteer-${o.session.id}-${o.attendee_id}`}
                className="text-xs font-semibold rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-3 py-1.5 disabled:opacity-50"
              >
                {busy[key] ? "…" : "I'll cover"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// "Upcoming Meetings & Trainings" — every future session the caller is
// scheduled to attend or cover, from today onward. Renders nothing when the
// list is empty so the dashboard stays uncluttered for off-cycle staff.
function MyUpcomingMeetings({ facilityId }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    if (!facilityId) return;
    setLoading(true);
    api.get(`/meetings/my-upcoming?facility_id=${facilityId}`)
      .then(({ data }) => { if (alive) setRows(data || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [facilityId]);

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white shadow-sm overflow-hidden"
      data-testid="dash-upcoming-meetings"
    >
      <div className="p-4 sm:p-5 border-b border-indigo-100 flex items-center gap-2">
        <CalendarClock size={16} className="text-indigo-600" />
        <div className="font-display font-semibold text-lg text-slate-900">
          Upcoming trainings & meetings
        </div>
        <span className="ml-auto text-xs text-slate-500 tabular-nums">
          {rows.length} scheduled
        </span>
      </div>
      <ul className="divide-y divide-indigo-100">
        {rows.map((r) => {
          const dObj = parseISO(r.date);
          const dateLine = format(dObj, "EEE, MMM d, yyyy");
          const isCover = r.role === "cover";
          return (
            <li
              key={`${r.meeting_id}:${r.session_id}:${r.role}`}
              className="p-3 sm:p-4 flex items-center gap-3"
              data-testid={`upcoming-meeting-${r.session_id}-${r.role}`}
            >
              <span
                className="w-1.5 h-11 rounded-full shrink-0"
                style={{ background: r.color || "#4f46e5" }}
                aria-hidden
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-slate-900 truncate">
                    {r.meeting_name}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${
                      isCover
                        ? "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}
                  >
                    {isCover ? "Covering" : "Attending"}
                  </span>
                  {r.status === "draft" && (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                      Draft
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 tabular-nums">
                  {dateLine} · {fmtTime(r.start_time)} – {fmtTime(r.end_time)}
                  {r.location && <span className="text-slate-500"> · {r.location}</span>}
                </div>
                {isCover && r.covers_name && (
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    Covering for {r.covers_name}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
